import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { NotificationContext } from "./NotificationContext";
import authAxios from "../api/authAxios";

const PermissionsContext = createContext();

const DEFAULT_PERMISSIONS = {
  SUPERADMIN: {
    // Has access to everything + super admin specific
    view_owner_dashboard: true,
    view_employee_dashboard: true,
    view_dashboard: true,
    manage_dashboard: true,
    view_customers: true,
    manage_customers: true,
    export_customers: true,
    import_customers: true,
    view_inventory: true,
    manage_inventory: true,
    export_inventory: true,
    import_inventory: true,
    view_pos: true,
    manage_pos: true,
    export_pos: true,
    view_invoices: true,
    manage_invoices: true,
    export_invoices: true,
    view_subscription: true,
    manage_subscription: true,
    manage_users: true,
    assign_roles: true,
    manage_settings: true,
    view_audit_logs: true,
    export_all: true,
    export_all: true,
    import_all: true,
    view_reports: true,
    export_reports: true,
    view_loyalty: true,
    manage_loyalty: true,
    view_support: true,
    view_vendors: true,
    create_vendors: true,
    edit_vendors: true,
    delete_vendors: true,
  },
  OWNER: {
    // Dashboard
    view_owner_dashboard: true,
    view_employee_dashboard: true,
    view_dashboard: true,
    manage_dashboard: true,
    edit_dashboard: true,
    // Customers
    view_customers: true,
    manage_customers: true,
    edit_customers: true,
    create_customers: true,
    export_customers: true,
    import_customers: true,
    // Inventory
    view_inventory: true,
    manage_inventory: true,
    edit_inventory: true,
    create_inventory: true,
    export_inventory: true,
    import_inventory: true,
    // POS Billing / Invoices
    view_pos: true,
    manage_pos: true,
    export_pos: true,
    view_invoices: true,
    manage_invoices: true,
    export_invoices: true,
    view_billing: true,
    edit_billing: true,
    export_billing: true,
    // Subscription / Payments
    view_subscription: true,
    manage_subscription: true,
    view_payments: true,
    edit_payments: true,
    // User Management / Employees
    manage_users: true,
    assign_roles: true,
    view_employees: true,
    edit_employees: true,
    create_employees: true,
    // Settings
    manage_settings: true,
    view_settings: true,
    view_audit_logs: true,
    // Data Management
    export_all: true,
    import_all: true,
    // Reports
    view_reports: true,
    export_reports: true,
    // Loyalty
    view_loyalty: true,
    manage_loyalty: true,
    // Support / Services
    view_support: true,
    view_services: true,
    // Quick Access
    quick_checkin: true,
    quick_billing: true,
    quick_invoices: true,
    quick_customers: true,
    quick_employees: true,
    quick_collections: true,
    quick_notifications: true,
    view_vendors: true,
    create_vendors: true,
    edit_vendors: true,
    delete_vendors: true,
  },
  SALES_EXECUTIVE: {
    // Dashboard
    view_owner_dashboard: false,
    view_employee_dashboard: true,
    view_dashboard: false,
    manage_dashboard: false,
    // Customers
    view_customers: true,
    manage_customers: true,
    export_customers: false,
    import_customers: false,
    // Inventory
    view_inventory: true,
    manage_inventory: true,
    create_inventory: true,
    edit_inventory: true,
    export_inventory: false,
    import_inventory: false,
    view_vendors: true,
    create_vendors: true,
    edit_vendors: true,
    delete_vendors: true,
    // POS Billing
    view_pos: true,
    manage_pos: true,
    export_pos: true,
    view_billing: true,
    edit_billing: true,
    export_billing: true,
    // Invoices
    view_invoices: false,
    manage_invoices: false,
    export_invoices: false,
    // Subscription
    view_subscription: false,
    manage_subscription: false,
    // User Management
    manage_users: false,
    assign_roles: false,
    // Settings
    manage_settings: false,
    view_audit_logs: false,
    // Data Management
    export_all: false,
    import_all: false,
    // Reports
    view_reports: false,
    export_reports: false,
    // Loyalty
    view_loyalty: false,
    manage_loyalty: false,
    // Support
    view_support: true,
    // Quick Access
    quick_checkin: true,
    quick_billing: true,
    quick_invoices: true,
    quick_customers: true,
    quick_employees: false,
    quick_collections: true,
    quick_notifications: true,
  },
  STAFF: {
    view_owner_dashboard: false,
    view_employee_dashboard: true,
    view_dashboard: false,
    manage_dashboard: false,
    view_customers: true,
    manage_customers: true,
    export_customers: false,
    import_customers: false,
    view_inventory: false,
    manage_inventory: false,
    export_inventory: false,
    import_inventory: false,
    view_vendors: true,
    create_vendors: true,
    edit_vendors: true,
    delete_vendors: true,
    view_pos: true,
    manage_pos: true,
    export_pos: false,
    view_invoices: false,
    manage_invoices: false,
    export_invoices: false,
    view_subscription: false,
    manage_subscription: false,
    manage_users: false,
    assign_roles: false,
    manage_settings: false,
    view_audit_logs: false,
    export_all: false,
    import_all: false,
    view_reports: false,
    export_reports: false,
    view_loyalty: false,
    manage_loyalty: false,
    view_support: true,
  },
  SALES: {
    view_owner_dashboard: false,
    view_employee_dashboard: true,
    view_dashboard: false,
    manage_dashboard: false,
    view_customers: true,
    manage_customers: true,
    export_customers: false,
    import_customers: false,
    view_inventory: false,
    manage_inventory: false,
    export_inventory: false,
    import_inventory: false,
    view_vendors: true,
    create_vendors: true,
    edit_vendors: true,
    delete_vendors: true,
    view_pos: true,
    manage_pos: true,
    export_pos: true,
    view_invoices: false,
    manage_invoices: false,
    export_invoices: false,
    view_subscription: false,
    manage_subscription: false,
    manage_users: false,
    assign_roles: false,
    manage_settings: false,
    view_audit_logs: false,
    export_all: false,
    import_all: false,
    view_reports: false,
    export_reports: false,
    view_loyalty: false,
    manage_loyalty: false,
    view_support: true,
  },
  CASHIER: {
    view_owner_dashboard: false,
    view_employee_dashboard: true,
    view_dashboard: false,
    manage_dashboard: false,
    view_customers: true,
    manage_customers: true,
    export_customers: false,
    import_customers: false,
    view_inventory: false,
    manage_inventory: false,
    export_inventory: false,
    import_inventory: false,
    view_pos: true,
    manage_pos: true,
    export_pos: false,
    view_invoices: true,
    manage_invoices: false,
    export_invoices: false,
    view_subscription: false,
    manage_subscription: false,
    manage_users: false,
    assign_roles: false,
    manage_settings: false,
    view_audit_logs: false,
    export_all: false,
    import_all: false,
    view_reports: false,
    export_reports: false,
    view_loyalty: false,
    manage_loyalty: false,
    view_support: true,
  },
};

const ALL_PERMISSIONS = {
  OWNER: [
    // Dashboard
    { key: "view_owner_dashboard", label: "View Owner Dashboard", category: "Dashboard" },
    { key: "view_employee_dashboard", label: "View Employee Dashboard", category: "Dashboard" },
    { key: "manage_dashboard", label: "Manage Dashboard Widgets", category: "Dashboard" },
    // Customers
    { key: "view_customers", label: "View Customers", category: "Customers" },
    { key: "manage_customers", label: "Add/Edit/Delete Customers", category: "Customers" },
    { key: "export_customers", label: "Export Customers", category: "Customers" },
    { key: "import_customers", label: "Import Customers", category: "Customers" },
    // Inventory
    { key: "view_inventory", label: "View Inventory", category: "Inventory" },
    { key: "manage_inventory", label: "Add/Edit/Delete Inventory", category: "Inventory" },
    { key: "export_inventory", label: "Export Inventory", category: "Inventory" },
    { key: "import_inventory", label: "Import Inventory", category: "Inventory" },
    { key: "view_vendors", label: "View Vendors", category: "Vendors" },
    { key: "create_vendors", label: "Create Vendors", category: "Vendors" },
    { key: "edit_vendors", label: "Edit Vendors", category: "Vendors" },
    { key: "delete_vendors", label: "Delete Vendors", category: "Vendors" },
    // POS Billing
    { key: "view_pos", label: "View POS Billing", category: "POS" },
    { key: "manage_pos", label: "Manage POS Billing", category: "POS" },
    { key: "export_pos", label: "Export POS Data", category: "POS" },
    // Invoices
    { key: "view_invoices", label: "View Invoices", category: "Invoices" },
    { key: "manage_invoices", label: "Add/Edit/Delete Invoices", category: "Invoices" },
    { key: "export_invoices", label: "Export Invoices", category: "Invoices" },
    // Subscription
    { key: "view_subscription", label: "View Subscription", category: "Subscription" },
    { key: "manage_subscription", label: "Manage Subscription Plans", category: "Subscription" },
    // User Management
    { key: "manage_users", label: "Add/Edit/Delete Users", category: "Users" },
    { key: "assign_roles", label: "Assign User Roles", category: "Users" },
    // Settings
    { key: "manage_settings", label: "Manage Application Settings", category: "Settings" },
    { key: "view_audit_logs", label: "View Audit Logs", category: "Settings" },
    // Data Management
    { key: "export_all", label: "Export All Data", category: "Data" },
    { key: "import_all", label: "Import All Data", category: "Data" },
    // Reports
    { key: "view_reports", label: "View Reports", category: "Reports" },
    { key: "export_reports", label: "Export Reports", category: "Reports" },
    // Loyalty
    { key: "view_loyalty", label: "View Loyalty Program", category: "Loyalty" },
    { key: "manage_loyalty", label: "Manage Loyalty Program", category: "Loyalty" },
    // Support
    { key: "view_support", label: "View Support", category: "Support" },
    // Quick Access
    { key: "quick_checkin", label: "Quick Access - Check In / Out", category: "Quick Access" },
    { key: "quick_billing", label: "Quick Access - POS Billing", category: "Quick Access" },
    { key: "quick_invoices", label: "Quick Access - Invoice History", category: "Quick Access" },
    { key: "quick_customers", label: "Quick Access - Customers", category: "Quick Access" },
    { key: "quick_employees", label: "Quick Access - Manage Employees", category: "Quick Access" },
    { key: "quick_collections", label: "Quick Access - Collections", category: "Quick Access" },
    { key: "quick_notifications", label: "Quick Access - Notifications", category: "Quick Access" },
  ],
  SALES_EXECUTIVE: [
    // Dashboard
    { key: "view_owner_dashboard", label: "View Owner Dashboard", category: "Dashboard" },
    { key: "view_employee_dashboard", label: "View Employee Dashboard", category: "Dashboard" },
    { key: "manage_dashboard", label: "Manage Dashboard Widgets", category: "Dashboard" },
    // Customers
    { key: "view_customers", label: "View Customers", category: "Customers" },
    { key: "manage_customers", label: "Add/Edit/Delete Customers", category: "Customers" },
    { key: "export_customers", label: "Export Customers", category: "Customers" },
    { key: "import_customers", label: "Import Customers", category: "Customers" },
    // Inventory
    { key: "view_inventory", label: "View Inventory", category: "Inventory" },
    { key: "manage_inventory", label: "Add/Edit/Delete Inventory", category: "Inventory" },
    { key: "export_inventory", label: "Export Inventory", category: "Inventory" },
    { key: "import_inventory", label: "Import Inventory", category: "Inventory" },
    { key: "view_vendors", label: "View Vendors", category: "Vendors" },
    { key: "create_vendors", label: "Create Vendors", category: "Vendors" },
    { key: "edit_vendors", label: "Edit Vendors", category: "Vendors" },
    { key: "delete_vendors", label: "Delete Vendors", category: "Vendors" },
    // POS Billing
    { key: "view_pos", label: "View POS Billing", category: "POS" },
    { key: "manage_pos", label: "Manage POS Billing", category: "POS" },
    { key: "export_pos", label: "Export POS Data", category: "POS" },
    // Invoices
    { key: "view_invoices", label: "View Invoices", category: "Invoices" },
    { key: "manage_invoices", label: "Add/Edit/Delete Invoices", category: "Invoices" },
    { key: "export_invoices", label: "Export Invoices", category: "Invoices" },
    // Subscription (Sales Executive cannot manage)
    { key: "view_subscription", label: "View Subscription", category: "Subscription" },
    { key: "manage_subscription", label: "Manage Subscription Plans", category: "Subscription" },
    // User Management (Sales Executive has no user management)
    { key: "manage_users", label: "Add/Edit/Delete Users", category: "Users" },
    { key: "assign_roles", label: "Assign User Roles", category: "Users" },
    // Settings
    { key: "manage_settings", label: "Manage Application Settings", category: "Settings" },
    { key: "view_audit_logs", label: "View Audit Logs", category: "Settings" },
    // Data Management
    { key: "export_all", label: "Export All Data", category: "Data" },
    { key: "import_all", label: "Import All Data", category: "Data" },
    // Reports
    { key: "view_reports", label: "View Reports", category: "Reports" },
    { key: "export_reports", label: "Export Reports", category: "Reports" },
    // Loyalty
    { key: "view_loyalty", label: "View Loyalty Program", category: "Loyalty" },
    { key: "manage_loyalty", label: "Manage Loyalty Program", category: "Loyalty" },
    // Support
    { key: "view_support", label: "View Support", category: "Support" },
    // Quick Access
    { key: "quick_checkin", label: "Quick Access - Check In / Out", category: "Quick Access" },
    { key: "quick_billing", label: "Quick Access - POS Billing", category: "Quick Access" },
    { key: "quick_invoices", label: "Quick Access - Invoice History", category: "Quick Access" },
    { key: "quick_customers", label: "Quick Access - Customers", category: "Quick Access" },
    { key: "quick_employees", label: "Quick Access - Manage Employees", category: "Quick Access" },
    { key: "quick_collections", label: "Quick Access - Collections", category: "Quick Access" },
    { key: "quick_notifications", label: "Quick Access - Notifications", category: "Quick Access" },
  ],
  STAFF: [
    { key: "view_customers", label: "View Customers", category: "Customers" },
    { key: "manage_customers", label: "Add/Edit Customers", category: "Customers" },
    { key: "view_pos", label: "View POS Billing", category: "POS" },
    { key: "manage_pos", label: "Manage POS Billing", category: "POS" },
    { key: "view_support", label: "View Support", category: "Support" },
  ],
  SALES: [
    { key: "view_customers", label: "View Customers", category: "Customers" },
    { key: "manage_customers", label: "Add/Edit Customers", category: "Customers" },
    { key: "view_pos", label: "View POS Billing", category: "POS" },
    { key: "manage_pos", label: "Manage POS Billing", category: "POS" },
    { key: "export_pos", label: "Export POS Data", category: "POS" },
    { key: "view_support", label: "View Support", category: "Support" },
  ],
  CASHIER: [
    { key: "view_customers", label: "View Customers", category: "Customers" },
    { key: "view_pos", label: "View POS Billing", category: "POS" },
    { key: "manage_pos", label: "Manage POS Billing", category: "POS" },
    { key: "view_invoices", label: "View Invoices", category: "Invoices" },
    { key: "view_support", label: "View Support", category: "Support" },
  ],
};
export function PermissionsProvider({ children }) {
  const { user, userRole, switchRole } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(true);

  // 1. Fetch Roles & Permissions matrix from Django backend
  useEffect(() => {
    const hasStoredSession = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!user) {
      setPermissions(DEFAULT_PERMISSIONS);
      if (!hasStoredSession) {
        setLoading(false);
      }
      return;
    }

    const applyMatrix = (matrix) => {
      setPermissions(prev => {
        const newPerms = JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS));
        Object.keys(matrix).forEach((roleName) => {
          const role = roleName.toUpperCase();
          const enabledPerms = matrix[roleName];

          if (!newPerms[role]) {
            newPerms[role] = {};
          }

          const possiblePerms = new Set();
          if (DEFAULT_PERMISSIONS[role]) {
            Object.keys(DEFAULT_PERMISSIONS[role]).forEach(k => possiblePerms.add(k));
          }
          if (ALL_PERMISSIONS[role]) {
            ALL_PERMISSIONS[role].forEach(p => possiblePerms.add(p.key));
          }

          enabledPerms.forEach(p => possiblePerms.add(p));

          possiblePerms.forEach((permKey) => {
            newPerms[role][permKey] = enabledPerms.includes(permKey);
          });
        });
        return newPerms;
      });
    };

    const fetchPermissions = async () => {
      try {
        const cached = sessionStorage.getItem("rbac_matrix_cache");
        if (cached) {
          try {
            const matrix = JSON.parse(cached);
            applyMatrix(matrix);
            setLoading(false);
          } catch (e) {
            sessionStorage.removeItem("rbac_matrix_cache");
          }
        } else {
          setLoading(true);
        }

        const response = await authAxios.get('/api/users/roles/matrix/', { timeout: 15000 });
        const matrix = response.data;
        sessionStorage.setItem("rbac_matrix_cache", JSON.stringify(matrix));
        applyMatrix(matrix);
      } catch (err) {
        if (err.code !== 'ECONNABORTED' && err.code !== 'ERR_CANCELED' && err.code !== 'ERR_NETWORK_IO_SUSPENDED') {
          console.warn("[Django RBAC] Could not sync permission matrix from server:", err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user]);

  // 2. Toggle Role Permission and write directly to Django API
  const togglePermission = async (role, permissionKey) => {
    const isEnabled = !permissions[role]?.[permissionKey];
    console.log(`[Django RBAC] Toggling permission: Role: ${role}, Key: ${permissionKey}, New State: ${isEnabled}`);

    // Update local state optimistically to feel instant
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [permissionKey]: isEnabled
      }
    }));

    try {
      // Save changes immediately to Django Backend API
      await authAxios.post('/api/users/roles/matrix/', {
        role: role,
        permission: permissionKey,
        enabled: isEnabled
      });
      console.log(`[Django Sync] Django API write success for Role: ${role}, Key: ${permissionKey}`);
      addNotification(`Permission ${permissionKey} updated to ${isEnabled ? "ON" : "OFF"}!`, "success");
    } catch (error) {
      console.error(`[Django Sync] Django update failed:`, error);
      addNotification("Failed to update permission in Django backend. Reverting changes.", "error");

      // Revert on error
      setPermissions(prev => ({
        ...prev,
        [role]: {
          ...prev[role],
          [permissionKey]: !isEnabled
        }
      }));
    }
  };

  const hasPermission = (passedRole, permissionKey) => {
    // Basic normalization
    const uRole = (passedRole || userRole)?.toUpperCase();

    if (!uRole) {
      console.warn(`hasPermission: No role found for check ${permissionKey}`);
      return false;
    }

    // Owner and Admin bypass: always has permission
    if (['OWNER', 'SUPER_ADMIN', 'SUPERADMIN', 'ADMIN'].includes(uRole)) {
      return true;
    }

    // Map legacy keys to DB permission codes
    const LEGACY_TO_DB_MAP = {
      // Dashboard
      view_owner_dashboard: 'view_owner_dashboard',
      view_employee_dashboard: 'view_employee_dashboard',
      view_dashboard: 'view_employee_dashboard',
      manage_dashboard: 'edit_dashboard',
      // Customers
      view_customers: 'view_customers',
      manage_customers: 'edit_customers',
      export_customers: 'export_customers',
      import_customers: 'create_customers',
      // Inventory
      view_inventory: 'view_inventory',
      manage_inventory: 'edit_inventory',
      export_inventory: 'export_inventory',
      import_inventory: 'create_inventory',
      // POS Billing
      view_pos: 'view_billing',
      manage_pos: 'edit_billing',
      export_pos: 'export_billing',
      // Invoices
      view_invoices: 'view_billing',
      manage_invoices: 'edit_billing',
      export_invoices: 'export_billing',
      // Subscription / Payments
      view_subscription: 'view_payments',
      manage_subscription: 'edit_payments',
      // User Management
      manage_users: 'view_employees',
      assign_roles: 'edit_employees',
      // Settings
      manage_settings: 'view_settings',
      view_audit_logs: 'view_settings',
      // Reports
      view_reports: 'view_reports',
      export_reports: 'export_reports',
      // Loyalty
      view_loyalty: 'view_customers',
      manage_loyalty: 'edit_customers',
      // Support
      view_support: 'view_settings'
    };

    const targetKey = LEGACY_TO_DB_MAP[permissionKey] || permissionKey;

    // Check if targetKey is enabled in permissions[uRole]
    let match = permissions[uRole]?.[targetKey];

    // Fallback to checking the original permissionKey if targetKey is undefined (e.g. before API load)
    if (match === undefined) {
      match = permissions[uRole]?.[permissionKey] ?? false;
    }

    return match;
  };

  const getPermissions = (userRole) => {
    return permissions[userRole] || {};
  };

  const resetToDefaults = async (role) => {
    // This would need a backend endpoint to reset or we loop through default state and post updates
    // For now, let's just log or implement a client-side loop if critical, or skip
    console.warn("Reset to defaults via API not yet implemented fully");
    setPermissions((prev) => ({
      ...prev,
      [role]: DEFAULT_PERMISSIONS[role],
    }));
  };

  const resetAllPermissions = () => {
    setPermissions(DEFAULT_PERMISSIONS);
  };

  const enableAllPermissionsForSubscribedRole = (role) => {
    // Enable all permissions for a subscribed role
    setPermissions((prev) => {
      const allPermissions = { ...prev[role] };
      // Set all permissions to true except those that are explicitly admin-only
      Object.keys(allPermissions).forEach((perm) => {
        // Keep subscription management disabled for ADMIN
        if (role === "OWNER" && (perm === "view_subscription" || perm === "manage_subscription")) {
          allPermissions[perm] = false;
        } else {
          allPermissions[perm] = true;
        }
      });
      return {
        ...prev,
        [role]: allPermissions,
      };
    });
  };

  const value = {
    permissions,
    loading,
    togglePermission,
    hasPermission,
    getPermissions,
    resetToDefaults,
    resetAllPermissions,
    enableAllPermissionsForSubscribedRole,
    ALL_PERMISSIONS,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionsProvider");
  }
  return context;
}
